import {
  PayableRow,
  UpdatePayableWorkflowStatusRequestPayerApprovalStatus
} from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache } from "@ws-react-query";
import { payablesApi } from "@api";
import { queriesPayableRows, queriesPayables } from "@query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../BulkActions";
import { QUERY_PAYABLE } from "../../../query/payments/keys";

const invalidateAfterMutation = (successItems: PayableRow[]) => {
  WSQueryCache.invalidateQueries(
    queriesPayableRows.searchPayableRows.queryKeyBase
  );
  WSQueryCache.invalidateQueries(queriesPayables.getPayable.queryKeyBase);
  successItems.forEach(payable => {
    if (payable.id) {
      WSQueryCache.invalidateQueries([QUERY_PAYABLE, payable.id]);
    }
  });
};

const processPreApprovePayables = (
  items: PayableRow[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> => {
  const bulkAction = bulkActionFactory<PayableRow>({
    onProgress,
    action: async item => {
      if (!item.id) {
        throw new Error("Payable is missing id");
      }
      return payablesApi.updatePayableWorkflowStatus({
        payableId: item.id,
        updatePayableWorkflowStatusRequest: {
          payerApprovalStatus:
            UpdatePayableWorkflowStatusRequestPayerApprovalStatus.PRE_APPROVED
        }
      });
    },
    onFinish: ({ successItems }) => {
      invalidateAfterMutation(successItems);
      onSuccess?.();
    }
  });

  return bulkAction.run(items).then(({ successItems, failedItems }) => ({
    total: items.length,
    processed: successItems.length + failedItems.length,
    successful: successItems.length,
    failed: failedItems.length
  }));
};

export const useBulkPreApprovePayables = () => {
  const runBulkAction = useForegroundBulkAction();

  return (items: PayableRow[], onSuccess?: () => void) => {
    const pluralized = `payable${items.length > 1 ? "s" : ""}`;

    return runBulkAction({
      actionName: "Pre-approve payables",
      itemCount: items.length,
      confirmDescription: `Pre-approve ${pluralized} to be approved`,
      confirmLabel: "Pre-approve",
      prepare: async () => items,
      process: async (data, onProgress) =>
        processPreApprovePayables(data, onProgress, onSuccess)
    });
  };
};
